import * as React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import SidePanel from 'src/components/sidePanel/SidePanel';
import { useListCardsInParking } from 'src/features/cards/api/useListCardsInParking';
import { type InParkingCard } from 'src/features/cards/types/card';
import { cardMessages } from 'src/features/cards/utils/messages';
import { useListPoints } from 'src/features/points/api/useListPoints';
import { ValidationDetails } from 'src/features/validation/components/ValidationDetails';
import { badgeValidationOptions } from 'src/features/validation/utils/constants';
import { validationMessages } from 'src/features/validation/utils/messages';
import useFilters from 'src/hooks/useFilters';
import usePagination from 'src/hooks/usePagination';
import useSearch from 'src/hooks/useSearch';
import useSorting from 'src/hooks/useSorting';
import { useParking } from 'src/providers/ParkingProvider';
import {
    CellDatetime,
    CellBadge,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useRowSelection from 'src/registry/hooks/useRowSelection';
import { generalMessages } from 'src/utils/messages';

const PARKING_VALIDATION_PANEL_NAME = 'parking-validation-panel';

const Validation = () => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();
    const { clearRowSelection, ...rowSelection } = useRowSelection({ sidePanelName: PARKING_VALIDATION_PANEL_NAME });
    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'enter_datetime', desc: true }],
    });
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'card_number' });
    const [uniqueZones, setUniqueZones] = React.useState<{ parking_id: string; parking_name: string }[] | undefined>();

    const { data, isLoading, isError, refetch } = useListCardsInParking({
        ...querySortingOptions,
        ...queryPaginationOptions,
        ...queryFiltersOptions,
        ...querySearchOptions,
        parkingId,
    });

    const { data: points } = useListPoints({ parkingId });

    React.useEffect(() => {
        if (!uniqueZones && data?.results?.length) {
            const zones = [
                ...new Map(
                    data.results.map(({ parking_id, parking_name }) => [parking_id, { parking_id, parking_name }]),
                ).values(),
            ];
            setUniqueZones(zones);
        }
    }, [data, uniqueZones]);

    const columnHelper = createColumnHelper<InParkingCard>();

    const columns = [
        columnHelper.accessor('plate_number', {
            header: DataTableColumnHeader,
            enableSorting: true,
            meta: {
                title: formatMessage(cardMessages.cardNumber),
            },
        }),
        columnHelper.accessor('card_number', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(cardMessages.ticket),
            },
        }),
        columnHelper.accessor('parking_name', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                title: formatMessage(cardMessages.parkingZone),
                options: uniqueZones?.map((zone) => ({ value: zone.parking_id, label: zone.parking_name })),
            },
            id: 'parking',
        }),
        columnHelper.accessor('enter_datetime', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(cardMessages.enterDatetime),
            },
        }),
        columnHelper.accessor('entry_point', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                title: formatMessage(cardMessages.entryPoint),
                options: points?.results.map((point) => ({ value: point.name, label: point.name })),
            },
        }),
        columnHelper.accessor((row) => `${row.card_type}-${row.is_guest}`, {
            header: DataTableColumnHeader,
            cell: (props) => <CellBadge badgeOptions={badgeValidationOptions} {...props} />,
            enableHiding: true,
            meta: {
                title: formatMessage(validationMessages.canBeValidated),
                badgeOptions: badgeValidationOptions,
            },
            id: 'canBeValidated',
        }),
    ];

    const getRowData = React.useMemo(() => {
        const firstSelected = Object.keys(rowSelection.rowSelectionState)[0];
        if (!data || !firstSelected) return;
        return data.results.find((cards) => cards.id === parseInt(firstSelected));
    }, [data, rowSelection.rowSelectionState]);

    return (
        <>
            <DataTable
                columns={columns}
                data={data}
                pagination={pagination}
                sorting={sorting}
                filters={filters}
                search={search}
                isError={isError}
                isLoading={isLoading}
                rowSelection={rowSelection}
                getRowId={(row) => row.id.toString()}
                components={{
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                }}
                semantics={{
                    title: formatMessage(validationMessages.validation),
                    entryName: formatMessage(generalMessages.vehicle),
                    entryNames: formatMessage(generalMessages.vehicles),
                    placeholder: formatMessage(cardMessages.cardNumber),
                    sidePanelName: PARKING_VALIDATION_PANEL_NAME,
                }}
            />
            <SidePanel onClose={clearRowSelection} name={PARKING_VALIDATION_PANEL_NAME}>
                <ValidationDetails inParkingCard={getRowData} refetch={refetch} />
            </SidePanel>
        </>
    );
};

export default Validation;
